import type {PredictionBatchSource} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';

import {MODEL_CATEGORY, type ScientificModelNode} from '../ScientificModelMetadata';

export const PREDICTION_CONFIG_SCHEMA_VERSION_V1 = 1;

export interface AaSequenceIdInputComponent {
  _type: 'aaSequenceId';
  id: string;
}

export interface MoleculeIdInputComponent {
  _type: 'moleculeId';
  id: string;
}

export interface ProteinIdInputComponent {
  _type: 'proteinId';
  id: string;
}

export interface AminoAcidsInputComponent {
  _type: 'aminoAcids';
  value: string;
}

export interface SmilesInputComponent {
  _type: 'smiles';
  value: string;
}

export type IdPredictionInputComponent =
  | AaSequenceIdInputComponent
  | MoleculeIdInputComponent
  | ProteinIdInputComponent;

export type RawPredictionInputComponent = AminoAcidsInputComponent | SmilesInputComponent;

export type PredictionInputComponent = IdPredictionInputComponent | RawPredictionInputComponent;

export function aaSequenceId(id: string): AaSequenceIdInputComponent {
  return {_type: 'aaSequenceId', id};
}

export function moleculeId(id: string): MoleculeIdInputComponent {
  return {_type: 'moleculeId', id};
}

export function proteinId(id: string): ProteinIdInputComponent {
  return {_type: 'proteinId', id};
}

export interface BulkPredictionItem {
  id: string;
  entityType: string;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function predictionInputComponentFromUnknown(component: unknown): PredictionInputComponent | null {
  if (component == null || typeof component !== 'object' || Array.isArray(component)) {
    return null;
  }
  const record = component as Record<string, unknown>;
  if (record._type === 'aaSequenceId') {
    const id = nonEmptyString(record.id);
    return id != null ? {_type: record._type, id} : null;
  }
  if (record._type === 'moleculeId') {
    const id = nonEmptyString(record.id);
    return id != null ? {_type: record._type, id} : null;
  }
  if (record._type === 'proteinId') {
    const id = nonEmptyString(record.id);
    return id != null ? {_type: record._type, id} : null;
  }
  if (record._type === 'aminoAcids') {
    const value = nonEmptyString(record.value);
    return value != null ? {_type: record._type, value} : null;
  }
  if (record._type === 'smiles') {
    const value = nonEmptyString(record.value);
    return value != null ? {_type: record._type, value} : null;
  }
  return null;
}

function extractPredictionInputComponentsFromUnknown(components: unknown): Array<PredictionInputComponent> {
  if (Array.isArray(components)) {
    return components
      .map((component) => predictionInputComponentFromUnknown(component))
      .filter((component): component is PredictionInputComponent => component != null);
  }
  const component = predictionInputComponentFromUnknown(components);
  return component != null ? [component] : [];
}

export type PredictionInputComponentsField = 'component' | 'components' | 'targets';

export function getPredictionInputComponentsField(
  model: ScientificModelNode
): PredictionInputComponentsField {
  if (model.category === MODEL_CATEGORY.GENERATIVE) {
    return 'targets';
  }
  if (model.capabilities.supportsMultimers) {
    return 'components';
  }
  return 'component';
}

export function extractPredictionInputComponents(
  configJson: Record<string, unknown>,
  model: ScientificModelNode
): Array<PredictionInputComponent> {
  const field = getPredictionInputComponentsField(model);
  return extractPredictionInputComponentsFromUnknown(configJson[field]);
}

export function predictionInputComponentFromEntityId(entityId: string): IdPredictionInputComponent {
  if (APIUtil.isMolecule(entityId)) {
    return moleculeId(entityId);
  }
  // APIUtil.isProtein refers to legacy AA sequences with prtn_ IDs.
  if (APIUtil.isProtein(entityId)) {
    return aaSequenceId(entityId);
  }
  // New Protein inputs use the generic basic folder item bfi_ prefix.
  if (APIUtil.isBasicFolderItem(entityId)) {
    return proteinId(entityId);
  }
  throw new Error(`Unsupported prediction input component entity ID: ${entityId}`);
}

export function resolvePredictionInputComponents(
  componentApiIds: ReadonlyArray<string>,
  configJson: Record<string, unknown> | null | undefined,
  model: ScientificModelNode
): Array<PredictionInputComponent> {
  if (configJson != null) {
    const fromConfig = extractPredictionInputComponents(configJson, model);
    if (fromConfig.length > 0) {
      return fromConfig;
    }
  }
  return componentApiIds.map((apiId) => predictionInputComponentFromEntityId(apiId));
}

export function isAminoAcidsInputComponent(
  component: PredictionInputComponent
): component is AminoAcidsInputComponent {
  return component._type === 'aminoAcids';
}

export function isSmilesInputComponent(
  component: PredictionInputComponent
): component is SmilesInputComponent {
  return component._type === 'smiles';
}

export function isAaSequenceIdInputComponent(
  component: PredictionInputComponent
): component is AaSequenceIdInputComponent {
  return component._type === 'aaSequenceId';
}

export function isMoleculeIdInputComponent(
  component: PredictionInputComponent
): component is MoleculeIdInputComponent {
  return component._type === 'moleculeId';
}

export function isProteinIdInputComponent(
  component: PredictionInputComponent
): component is ProteinIdInputComponent {
  return component._type === 'proteinId';
}

export function isRawPredictionInputComponent(
  component: PredictionInputComponent
): component is RawPredictionInputComponent {
  return isAminoAcidsInputComponent(component) || isSmilesInputComponent(component);
}

export function isIdPredictionInputComponent(
  component: PredictionInputComponent
): component is IdPredictionInputComponent {
  return (
    isAaSequenceIdInputComponent(component) ||
    isMoleculeIdInputComponent(component) ||
    isProteinIdInputComponent(component)
  );
}

export function getRawModelInputChipDisplayLabel(component: RawPredictionInputComponent): string {
  return isAminoAcidsInputComponent(component) ? 'AA Sequence' : 'Molecule';
}

export function getRawComponentLabelPrefix(
  component: RawPredictionInputComponent,
  rawEntryIndex: number,
  useNumberedLabels: boolean
): string {
  const baseLabel = isAminoAcidsInputComponent(component) ? 'Sequence' : 'Ligand';
  return useNumberedLabels ? `${baseLabel} ${rawEntryIndex + 1}` : baseLabel;
}

// Molecules are excluded here because bulk predictions only support monomers and single-entity
// inputs; molecules are only meaningful as part of multimers, which are not yet supported in bulk.
const INPUT_TYPE_TO_FOLDER_ITEM_TYPE: Record<string, OligoAwareFolderItemType> = {
  amino_acid_sequence: ModelTypeConstants.PROTEIN,
};

export function getAvailableTypesForModel(inputTypes: Array<string>): Array<OligoAwareFolderItemType> {
  return inputTypes
    .map((t) => INPUT_TYPE_TO_FOLDER_ITEM_TYPE[t])
    .filter((t): t is OligoAwareFolderItemType => t != null);
}

export function isEntityTypeCompatible(entityType: string, inputTypes: Array<string>): boolean {
  const compatibleFolderItemTypes = getAvailableTypesForModel(inputTypes);
  return compatibleFolderItemTypes.includes(entityType as OligoAwareFolderItemType);
}

export type PredictionConfig = Record<string, unknown>;

type PredictionConfigBuilder = (item: BulkPredictionItem) => PredictionConfig;

interface PredictionJobConfig {
  config: PredictionConfig;
  name?: string | null;
}

const aaSequenceConfigBuilder: PredictionConfigBuilder = (item) => ({
  component: aaSequenceId(item.id),
});
const boltzGenConfigBuilder: PredictionConfigBuilder = (item) => ({
  targets: [item.entityType === ModelTypeConstants.MOLECULE ? moleculeId(item.id) : aaSequenceId(item.id)],
});
const multimerListConfigBuilder: PredictionConfigBuilder = (item) => ({
  components: [aaSequenceId(item.id)],
});

function getPredictionConfigBuilder(model: ScientificModelNode): PredictionConfigBuilder {
  const field = getPredictionInputComponentsField(model);
  if (field === 'targets') {
    return boltzGenConfigBuilder;
  }
  if (field === 'components') {
    return multimerListConfigBuilder;
  }
  return aaSequenceConfigBuilder;
}

export function buildPredictionConfigs(
  model: ScientificModelNode,
  items: Array<BulkPredictionItem>
): Array<PredictionConfig> {
  const builder = getPredictionConfigBuilder(model);
  return items.map((item) => builder(item));
}

export function buildPredictionBatchName(model: ScientificModelNode): string {
  return `${model.displayName} run (1.0)`;
}

export interface BulkPredictionSubmitParams {
  folderId?: string;
  model: ScientificModelNode;
  // Optional: the New prediction flow may omit this so the backend generates a default batch name
  // (via /model-hub/prediction-batch:generate-name).
  name?: string;
  items?: Array<BulkPredictionItem>;
  predictionConfigs?: Array<PredictionConfig>;
  // Optional per-job names, positionally aligned with predictionConfigs/items.
  jobNames?: Array<string | null>;
  modelParams?: Record<string, unknown>;
  source?: PredictionBatchSource;
}

export async function submitBulkPrediction(
  params: BulkPredictionSubmitParams
): Promise<{predictionBatchId: string}> {
  const {folderId, model, items, modelParams, source, name, jobNames} = params;
  if (params.predictionConfigs == null && items == null) {
    throw new Error('Must provide items or prediction configs');
  }
  if (params.predictionConfigs != null && items != null) {
    throw new Error('Must provide only one of items or prediction configs');
  }
  const predictionConfigs = params.predictionConfigs ?? buildPredictionConfigs(model, items ?? []);

  const jobConfigs: Array<PredictionJobConfig> = predictionConfigs.map((predictionConfig, index) => {
    const jobName = jobNames?.[index];
    return {
      config: {
        ...predictionConfig,
        ...(modelParams === undefined ? {} : modelParams),
      },
      ...(jobName != null && jobName !== '' ? {name: jobName} : {}),
    };
  });

  const data: Record<string, unknown> = {
    model: model.externalReference,
    jobConfigs,
    configSchemaVersion: PREDICTION_CONFIG_SCHEMA_VERSION_V1,
    source,
    name,
  };
  if (folderId != null) {
    data.folderId = folderId;
  }

  return await net.postJSONPromise({
    url: '/1/api/model-hub/prediction-batch',
    data,
  });
}
